/**
 * The Season Scorecard: your five against the board's five against the
 * Automatic, one row per week.
 *
 * Records are `[wins, losses, ties]`, the shape `/history` sends
 * `week_record` in, and they are in POOL POINTS: the commissioner prints a
 * perfect week as 7-0 (the +2 folded into wins -- CLAUDE.md, "The other
 * inbound"), so the picker's record arrives with the bonus already in it and
 * nothing here adds it again. The board's five are scored the same way a
 * picker's would be, so a board 5-0 reads 7-0 too and the columns compare like
 * with like. The Automatic's record is the sheet's own, verbatim: it is never
 * paid the bonus, so its 5-0 stays 5-0 and carries no marker.
 */

import { isMorningGame, passesMorningRule } from "./morningGame.js";

export const BOARD_SIZE = 5;
const PERFECT_BONUS = 2;

const ET_WEEKDAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  weekday: "short",
});

/** The kickoff's weekday in Eastern time ("Sun", "Thu", ...), or null. */
export function kickoffWeekday(kickoffAt) {
  if (!kickoffAt) return null;
  const date = new Date(kickoffAt);
  return Number.isNaN(date.getTime()) ? null : ET_WEEKDAY.format(date);
}

/**
 * Whether a game is on the Sunday/Monday slate. The picker never takes a
 * Thursday, Friday or Saturday game, so the board leaves them out to compare
 * like with like. Read in Eastern time: a Sunday-night kickoff is Monday in UTC.
 */
export function isSundayOrMonday(kickoffAt) {
  const day = kickoffWeekday(kickoffAt);
  return day === "Sun" || day === "Mon";
}

/**
 * The edge side graded against the frozen line -- the same grading as the Edge
 * report card: win, loss or push. Null until the line has settled.
 */
export function gradeBoardGame(game) {
  const ats = game.ats_result;
  if (!ats || !game.edge || !game.edge.side) return null;
  if (ats === "push") return "push";
  return ats === game.edge.side ? "win" : "loss";
}

function sideTeam(game, side) {
  return side === "home"
    ? { team: game.home_team, abbr: game.home_abbr }
    : { team: game.away_team, abbr: game.away_abbr };
}

/**
 * One week's games ranked the way the Picks page ranks them: Sunday/Monday
 * games with a frozen line and a measurable edge, by `edge.winProbability`
 * (read at the decision deadline -- `/history` only sends quotes from before
 * it), highest first. The top five are the board's five.
 *
 * A Sunday-morning game (`isMorningGame`) is decided at its own kickoff, the
 * instant `/history` prices it at, before the 1pm prices exist -- so it is on
 * the board only at or over `MORNING_GAME_BAR`, and then it takes its slot
 * first, ahead of the 1pm-and-later games. Ranking it against the 1pm games'
 * 1pm prices would be hindsight the picker never had.
 *
 * Games are expected already analysed (`analyzeHistoryGame`), so each carries
 * its `edge`.
 */
export function rankBoard(weekGames) {
  const morning = (game) => isMorningGame(game.kickoff_at);
  return weekGames
    .filter((game) =>
      game.league_spread != null &&
      game.edge && game.edge.measurable && game.edge.winProbability != null &&
      isSundayOrMonday(game.kickoff_at) &&
      passesMorningRule(game.kickoff_at, game.edge.winProbability)
    )
    .sort((a, b) =>
      (morning(b) - morning(a)) ||
      (b.edge.winProbability - a.edge.winProbability) ||
      (new Date(a.kickoff_at) - new Date(b.kickoff_at)) ||
      (a.id - b.id)
    )
    .map((game, i) => ({
      game,
      rank: i + 1,
      side: game.edge.side,
      ...sideTeam(game, game.edge.side),
      winProbability: game.edge.winProbability,
      result: gradeBoardGame(game),
    }));
}

/**
 * W-L-T over graded results, or null when none is graded yet. With
 * `bonus: true` a full five of five wins is scored the way the pool scores it,
 * 7-0.
 */
export function recordOf(results, { bonus = false } = {}) {
  const graded = results.filter(Boolean);
  if (!graded.length) return null;
  const wins = graded.filter((r) => r === "win").length;
  const losses = graded.filter((r) => r === "loss").length;
  const ties = graded.filter((r) => r === "push").length;
  const perfect = bonus && results.length === BOARD_SIZE && wins === BOARD_SIZE;
  return [perfect ? wins + PERFECT_BONUS : wins, losses, ties];
}

/** A record that carries the +2: 7-0, as the sheet prints it. */
export function isPerfect(record) {
  return Boolean(record) &&
    record[0] === BOARD_SIZE + PERFECT_BONUS && record[1] === 0 && !record[2];
}

export function formatRecord(record) {
  if (!record) return "—";
  const [w, l, t] = record;
  return t ? `${w}-${l}-${t}` : `${w}-${l}`;
}

/**
 * Where your five departed from the board's, as one short line:
 * "4 of 5 shared · you took CIN (#8) over HOU". Each pick of yours off the
 * board carries its rank in the same list; one on the other side of a ranked
 * game says so, and one the board never considered says why (a Thursday game,
 * or no edge to rank on).
 */
export function whatDiffered(myPicks, ranked, weekGames = []) {
  const five = ranked.slice(0, BOARD_SIZE);
  const shortBoard = five.length < BOARD_SIZE;
  const shortNote = five.length
    ? `board had only ${five.length} eligible game${five.length === 1 ? "" : "s"}`
    : "no board this week: no Sunday or Monday game with a frozen line and an edge";

  if (!five.length) return shortNote;

  if (!myPicks || !myPicks.length) {
    const board = `Board: ${five.map((pick) => pick.abbr).join(", ")}`;
    return shortBoard ? `${board} · ${shortNote}` : board;
  }

  const boardTeams = new Set(five.map((pick) => pick.team));
  const mine = myPicks.map((pick) => ({ team: pick.team, abbr: pick.abbr || pick.team }));
  const myTeams = new Set(mine.map((pick) => pick.team));
  const shared = mine.filter((pick) => boardTeams.has(pick.team)).length;

  const parts = [];
  if (shared === five.length && mine.length === five.length && !shortBoard) {
    parts.push("Same five as the board");
  } else {
    parts.push(`${shared} of ${five.length} shared`);
    const offBoard = mine
      .filter((pick) => !boardTeams.has(pick.team))
      .map((pick) => ({ ...pick, ...placeOf(pick.team, ranked, weekGames) }))
      .sort((a, b) => a.order - b.order);
    const skipped = five.filter((pick) => !myTeams.has(pick.team)).map((pick) => pick.abbr);

    if (offBoard.length) {
      const took = offBoard.map((pick) => `${pick.abbr} (${pick.label})`).join(", ");
      parts.push(skipped.length ? `you took ${took} over ${skipped.join(", ")}` : `you took ${took}`);
    } else if (skipped.length) {
      parts.push(`you passed on ${skipped.join(", ")}`);
    }
  }
  if (shortBoard) parts.push(shortNote);
  return parts.join(" · ");
}

function placeOf(team, ranked, weekGames) {
  const own = ranked.find((pick) => pick.team === team);
  if (own) return { label: `#${own.rank}`, order: own.rank };

  const fade = ranked.find((pick) => pick.game.home_team === team || pick.game.away_team === team);
  if (fade) return { label: `other side of #${fade.rank}`, order: fade.rank + 0.5 };

  const game = weekGames.find((g) => g.home_team === team || g.away_team === team);
  if (game && !isSundayOrMonday(game.kickoff_at)) {
    return { label: kickoffWeekday(game.kickoff_at) || "off-slate", order: Infinity };
  }
  if (
    game && isMorningGame(game.kickoff_at) &&
    game.league_spread != null && game.edge?.measurable &&
    !passesMorningRule(game.kickoff_at, game.edge.winProbability)
  ) {
    return { label: "morning, under bar", order: Infinity };
  }
  return { label: "unranked", order: Infinity };
}

/**
 * Which columns hold the row's best record, on wins (pool points). Ties
 * highlight every tied column; a column with no record never wins.
 */
export function bestColumns(records) {
  const scored = Object.entries(records).filter(([, record]) => record);
  if (scored.length < 2) return [];
  const top = Math.max(...scored.map(([, record]) => record[0]));
  return scored.filter(([, record]) => record[0] === top).map(([key]) => key);
}

function addRecord(total, record) {
  if (!record) return total;
  const base = total || [0, 0, 0];
  return [base[0] + record[0], base[1] + record[1], base[2] + (record[2] || 0)];
}

/**
 * The scorecard, one row per week that has something to score, ascending.
 *
 * `games` are analysed history games; `poolWeeks` is `/history`'s
 * `pool.weeks`. An estimated (interpolated) standings week carries no record
 * of its own, so it only appears if the board or the Automatic has one.
 */
export function scorecardRows(games, poolWeeks = []) {
  const gamesByWeek = new Map();
  for (const game of games) {
    if (!gamesByWeek.has(game.week)) gamesByWeek.set(game.week, []);
    gamesByWeek.get(game.week).push(game);
  }
  const poolByWeek = new Map(poolWeeks.map((week) => [week.week, week]));
  const weeks = [...new Set([...gamesByWeek.keys(), ...poolByWeek.keys()])].sort((a, b) => a - b);

  return weeks
    .map((week) => {
      const weekGames = gamesByWeek.get(week) || [];
      const pool = poolByWeek.get(week) || {};
      const ranked = rankBoard(weekGames);
      const five = ranked.slice(0, BOARD_SIZE);
      const records = {
        you: pool.week_record || null,
        board: recordOf(five.map((pick) => pick.result), { bonus: true }),
        auto: pool.automatic_record || null,
      };
      return {
        week,
        records,
        best: bestColumns(records),
        board: five,
        hasBoard: five.length > 0,
        differed: whatDiffered(pool.picks || [], ranked, weekGames),
      };
    })
    .filter((row) => row.records.you || row.records.board || row.records.auto);
}

/** Season totals for each column, summed in pool points. */
export function scorecardTotals(rows) {
  const records = rows.reduce(
    (totals, row) => ({
      you: addRecord(totals.you, row.records.you),
      board: addRecord(totals.board, row.records.board),
      auto: addRecord(totals.auto, row.records.auto),
    }),
    { you: null, board: null, auto: null }
  );
  return { records, best: bestColumns(records) };
}

/**
 * Where you went your own way, and how it went. Per week, your picks off the
 * board's five are your SWAPS and the board's picks you passed on are the
 * SKIPPED; each side's results are summed over the season, so "your swaps
 * 3-2, the app's picks you skipped 4-1" says whether going off the board paid.
 * Plain W-L-T (no bonus): these are single picks, not five-pick weeks.
 *
 * Only weeks with both your graded picks and a board count. `rows` is
 * `scorecardRows` output with each row's `picks` (your graded five) attached
 * by `withPicks`.
 */
export function swapSplit(rows) {
  const swaps = [];
  const skipped = [];
  let shared = 0;
  for (const row of rows) {
    const split = weekSwaps(row);
    if (!split) continue;
    swaps.push(...split.swaps.map((pick) => pick.result));
    skipped.push(...split.skipped.map((pick) => pick.result));
    shared += split.shared;
  }
  return {
    picks: swaps.length,
    shared,
    swaps: recordOf(swaps),
    skipped: recordOf(skipped),
  };
}

/** One week's swaps and skipped board picks, or null without both sides. */
export function weekSwaps(row) {
  const mine = (row.picks || []).filter((pick) => pick.team);
  if (!mine.length || !row.board || !row.board.length) return null;
  const boardTeams = new Set(row.board.map((pick) => pick.team));
  const myTeams = new Set(mine.map((pick) => pick.team));
  const swaps = mine.filter((pick) => !boardTeams.has(pick.team));
  return {
    swaps: swaps.map((pick) => ({ abbr: pick.abbr || pick.team, result: gradedResult(pick.result) })),
    skipped: row.board.filter((pick) => !myTeams.has(pick.team)).map((pick) => ({ abbr: pick.abbr, result: pick.result })),
    shared: mine.length - swaps.length,
  };
}

/** "Took MIN, LAR over DET, DAL" -- or null when you matched the board. */
export function swapNote(row) {
  const split = weekSwaps(row);
  if (!split || !split.swaps.length) return null;
  const took = split.swaps.map((pick) => pick.abbr).join(", ");
  return split.skipped.length ? `Took ${took} over ${split.skipped.map((pick) => pick.abbr).join(", ")}` : `Took ${took}`;
}

function gradedResult(result) {
  return ["win", "loss", "push"].includes(result) ? result : null;
}

/** `scorecardRows` with each week's graded picks from `pool.weeks` attached. */
export function withPicks(rows, poolWeeks = []) {
  const byWeek = new Map(poolWeeks.map((week) => [week.week, week]));
  return rows.map((row) => ({ ...row, picks: (byWeek.get(row.week) || {}).picks || [] }));
}
